import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Sparkles, AlertCircle, Lightbulb, Info, Send, Heart, MessageCircle,
         BarChart2, Plus, X, Loader2, ArrowUpDown, Flame, TrendingUp, TrendingDown,
         Cloud, Sun, CloudRain, Trophy, Activity, Smile, Pencil, Trash2, Check, Newspaper } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PhotoUploader, { PhotoStrip } from "@/components/PhotoUploader";
import Avatar from "@/components/Avatar";
import ChatPanel from "@/components/ChatPanel";
import { useAuth } from "@/lib/auth";

const tagMeta = {
  success:    { label: "Succès",     Icon: Sparkles,    color: "bg-emerald-100 text-emerald-700" },
  difficulty: { label: "Difficulté", Icon: AlertCircle, color: "bg-rose-100 text-rose-700" },
  idea:       { label: "Idée",       Icon: Lightbulb,   color: "bg-amber-100 text-amber-700" },
  info:       { label: "Info",       Icon: Info,        color: "bg-slate-100 text-slate-700" },
};
const EMOJIS = ["👍", "❤️", "🎉", "🔥", "💡", "😮"];
const MOODS = [
  { e: "😊", label: "Serein" }, { e: "🔥", label: "En feu" }, { e: "💪", label: "Motivé" },
  { e: "🎯", label: "Focus" }, { e: "🥰", label: "Reconnaissant" }, { e: "😌", label: "Zen" },
  { e: "😴", label: "Fatigué" }, { e: "😐", label: "Neutre" }, { e: "😤", label: "Sous tension" },
  { e: "🎉", label: "En célébration" },
];

function ReactionBar({ post, onReact, currentUserId }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const entries = Object.entries(post.reactions || {}).filter(([, arr]) => arr.length > 0);
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {entries.map(([emoji, arr]) => {
        const mine = arr.includes(currentUserId);
        return (
          <button key={emoji} onClick={() => onReact(emoji)} data-testid={`react-${post.id}-${emoji}`}
                  className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 transition ${
                    mine ? "bg-blue-100 border border-blue-300" : "bg-slate-100 border border-transparent hover:bg-slate-200"
                  }`}>
            <span>{emoji}</span><span className="font-semibold text-slate-700">{arr.length}</span>
          </button>
        );
      })}
      <div className="relative">
        <button data-testid={`react-add-${post.id}`} onClick={() => setPickerOpen(!pickerOpen)}
                className="text-xs px-2 py-1 rounded-full flex items-center gap-1 border bg-white text-slate-500 border-slate-200 hover:bg-slate-50">
          <Heart className="w-3 h-3"/> Réagir
        </button>
        {pickerOpen && (
          <div className="absolute z-20 top-full mt-1 left-0 bg-white border border-slate-200 rounded-full shadow-lg p-1 flex gap-0.5">
            {EMOJIS.map(e => (
              <button key={e} onClick={() => { onReact(e); setPickerOpen(false); }}
                      className="w-8 h-8 grid place-items-center rounded-full hover:bg-slate-100 text-lg">{e}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Poll({ post, onVote, currentUserId }) {
  const opts = post.poll?.options || [];
  const votes = post.poll?.votes || {};
  const total = Object.values(votes).reduce((s, a) => s + a.length, 0);
  return (
    <div className="mt-3 space-y-2">
      {opts.map((o, i) => {
        const count = (votes[String(i)] || []).length;
        const pct = total ? Math.round(count * 100 / total) : 0;
        const mine = (votes[String(i)] || []).includes(currentUserId);
        return (
          <button key={i} onClick={() => onVote(i)} data-testid={`poll-${post.id}-${i}`}
                  className={`w-full text-left rounded-xl border overflow-hidden relative transition ${mine ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
            <div className="absolute inset-y-0 left-0 bg-blue-100/70 transition-all" style={{ width: `${pct}%` }}/>
            <div className="relative px-3 py-2 flex items-center justify-between gap-2">
              <span className={`text-sm font-medium ${mine ? "text-blue-800" : "text-slate-700"}`}>{o}</span>
              <span className="text-xs font-bold text-slate-600">{pct}% <span className="text-slate-400">· {count}</span></span>
            </div>
          </button>
        );
      })}
      <div className="text-xs text-slate-400">{total} vote(s)</div>
    </div>
  );
}

function CommentSection({ post, usersById }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const loadC = async () => { try { const { data } = await api.get("/comments", { params: { entity_type: "journal", entity_id: post.id } }); setComments(data); } catch {} };
  const toggle = () => { setOpen(!open); if (!open) loadC(); };
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post("/comments", { entity_type: "journal", entity_id: post.id, text }); setText(""); loadC(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <button onClick={toggle} className="text-xs text-slate-500 hover:text-blue-600 flex items-center gap-1 mt-2">
        <MessageCircle className="w-3.5 h-3.5"/> {open ? "Masquer" : "Commenter"}
        {comments.length > 0 && <span className="font-semibold">({comments.length})</span>}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {comments.map(c => (
            <div key={c.id} className="flex gap-2 items-start">
              <Avatar user={usersById[c.user_id] || { name: c.user_name }} size={26}/>
              <div className="flex-1 text-xs bg-slate-50 rounded-2xl px-3 py-2">
                <div className="font-semibold text-slate-800">{c.user_name}
                  <span className="text-slate-400 font-normal ml-1">· {new Date(c.created_at).toLocaleString("fr-FR", { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" })}</span>
                </div>
                <div className="text-slate-600 mt-0.5">{c.text}</div>
              </div>
            </div>
          ))}
          <div className="flex gap-1">
            <input value={text} onChange={e => setText(e.target.value)} placeholder="Un commentaire ?" onKeyDown={e => e.key === "Enter" && send()}
                   className="flex-1 text-xs px-3 py-1.5 rounded-full border border-slate-200 focus:ring-2 focus:ring-blue-100 outline-none"/>
            <button onClick={send} className="px-3 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">Envoyer</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Journal() {
  const { user, refreshUser } = useAuth();
  const [sp, setSp] = useSearchParams();
  const initialTab = sp.get("tab") === "messagerie" ? "messagerie" : "fil";
  const [tab, setTab] = useState(initialTab);
  const initialChatId = sp.get("chat") || null;
  const initialPrefill = sp.get("prefill") || "";
  const initialRituelKey = sp.get("rituel") || null;
  const [unreadChats, setUnreadChats] = useState(0);
  const [unreadFil, setUnreadFil] = useState(0);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState("");
  const [location, setLocation] = useState("");
  const [tag, setTag] = useState("info");
  const [posting, setPosting] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [mode, setMode] = useState("post");
  const [pollOpts, setPollOpts] = useState(["", ""]);
  const [sort, setSort] = useState("recent");
  const [topPosts, setTopPosts] = useState([]);
  const [lastActivity, setLastActivity] = useState([]);
  const [weather, setWeather] = useState(null);
  const [topScores, setTopScores] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // { start, query } | null
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const textareaRef = useRef(null);

  const usersById = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const load = async () => {
    const [{ data: p }, { data: u }, { data: top }, { data: la }] = await Promise.all([
      api.get("/journal"), api.get("/users"), api.get("/journal/top-posts"), api.get("/users/last-activity"),
    ]);
    setPosts(p); setUsers(u); setTopPosts(top); setLastActivity(la);
    api.get("/weather/current").then(r => setWeather(r.data)).catch(() => {});
    api.get("/kpi/health-scores").then(r => setTopScores(r.data || [])).catch(() => {});
    // Poll unread chat count for the Messagerie badge
    api.get("/chats").then(({ data }) => {
      const total = (data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
      setUnreadChats(total);
    }).catch(() => setUnreadChats(0));
    // Compute "new posts" (last 24h) for the Fil badge
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    setUnreadFil(((p || [])).filter(x => new Date(x.created_at).getTime() > dayAgo && x.user_id !== user?.id).length);
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  // Detect @ mention typing → show suggestion list
  const onContentChange = (e) => {
    const val = e.target.value; setContent(val);
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([A-Za-zÀ-ÿ0-9\-_.]*)$/);
    if (m) setMentionQuery({ start: caret - m[1].length - 1, query: m[1].toLowerCase() });
    else setMentionQuery(null);
  };

  const insertMention = (u) => {
    if (!mentionQuery) return;
    const before = content.slice(0, mentionQuery.start);
    const after = content.slice((mentionQuery.start) + 1 + mentionQuery.query.length);
    const nameToken = u.name.split(" ")[0]; // first name for compactness
    const next = `${before}@${nameToken} ${after}`;
    setContent(next);
    setMentionQuery(null);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const mentionResults = useMemo(() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.query;
    return users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 6);
  }, [mentionQuery, users]);

  const submit = async () => {
    if (!content.trim() || !location.trim()) return toast.error("Lieu et contenu requis");
    if (mode === "poll" && pollOpts.filter(o => o.trim()).length < 2) return toast.error("Au moins 2 options");
    setPosting(true);
    try {
      await api.post("/journal/v2", { content, location, tag, photos, kind: mode, poll_options: mode === "poll" ? pollOpts : null });
      setContent(""); setLocation(""); setTag("info"); setPhotos([]); setPollOpts(["", ""]); setMode("post");
      toast.success("Publié !"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setPosting(false); }
  };

  const setMood = async (emoji) => {
    try { await api.post("/users/me/mood", { mood: emoji }); toast.success(`Humeur : ${emoji}`); refreshUser?.(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const react = async (post, emoji) => {
    try { const { data } = await api.post(`/journal/${post.id}/react`, { emoji }); setPosts(posts.map(p => p.id === post.id ? { ...p, reactions: data.reactions } : p)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const vote = async (post, i) => {
    try { const { data } = await api.post(`/journal/${post.id}/vote`, { option_index: i }); setPosts(posts.map(p => p.id === post.id ? { ...p, poll: data.poll } : p)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async (post) => {
    if (!editContent.trim()) return toast.error("Contenu vide");
    try {
      const { data } = await api.patch(`/journal/${post.id}`, { content: editContent });
      setPosts(posts.map(p => p.id === post.id ? { ...p, content: data.content, edited: data.edited } : p));
      setEditingId(null); setEditContent("");
      toast.success("Publication modifiée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePost = async (post) => {
    if (!confirm("Supprimer cette publication ? Cette action est irréversible.")) return;
    try {
      await api.delete(`/journal/${post.id}`);
      setPosts(posts.filter(p => p.id !== post.id));
      toast.success("Publication supprimée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Group posts by day
  const grouped = useMemo(() => {
    const arr = [...posts].sort((a, b) => sort === "recent" ? b.created_at.localeCompare(a.created_at) : a.created_at.localeCompare(b.created_at));
    const groups = {}; const now = new Date(); const yest = new Date(now); yest.setDate(now.getDate() - 1);
    arr.forEach(p => {
      const d = new Date(p.created_at);
      const isToday = d.toDateString() === now.toDateString();
      const isYest = d.toDateString() === yest.toDateString();
      const key = isToday ? "Aujourd'hui" : isYest ? "Hier" : d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
      (groups[key] = groups[key] || []).push(p);
    });
    return Object.entries(groups);
  }, [posts, sort]);

  // Top 10 corners par Wasa Score (health-score /100)
  const top10 = useMemo(() => [...topScores].sort((a, b) => b.score - a.score).slice(0, 10), [topScores]);

  const myMood = user?.mood?.emoji;

  return (
    <div className="max-w-6xl mx-auto space-y-4 pb-24 lg:pb-4" data-testid="journal-page">
      {/* Sticky top tab bar (Instagram-style) */}
      <div className="sticky top-[46px] z-20 -mx-4 sm:mx-0 bg-white/90 backdrop-blur-md border-b border-slate-200 sm:rounded-2xl sm:border sm:shadow-sm px-2 py-1.5" data-testid="wasagram-tabs-top">
        <div className="flex items-center gap-1">
          <TabButton active={tab === "fil"} onClick={() => setTab("fil")} testid="tab-fil"
                     icon={Newspaper} label="Fil d'actualité" badge={tab === "fil" ? 0 : unreadFil}/>
          <TabButton active={tab === "messagerie"} onClick={() => setTab("messagerie")} testid="tab-messagerie"
                     icon={MessageCircle} label="Messagerie" badge={tab === "messagerie" ? 0 : unreadChats}/>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${tab === "fil" ? "lg:grid-cols-3" : "lg:grid-cols-3"} gap-6`}>
        {/* MAIN COLUMN */}
        <div className="lg:col-span-2 space-y-4" data-testid={`wasagram-main-${tab}`}>
          {tab === "messagerie" ? (
            <div style={{ minHeight: 620 }}>
              <ChatPanel initialChatId={initialChatId} initialPrefill={initialPrefill} initialRituelKey={initialRituelKey}/>
            </div>
          ) : (<></>)}
          {tab === "fil" && (<>
          {/* Mood picker */}
          <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-emerald-50 border border-purple-100 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Smile className="w-4 h-4 text-purple-600"/>
              <div className="text-sm font-semibold text-slate-800">Comment vous sentez-vous aujourd'hui ?</div>
              {myMood && <span className="ml-auto text-xs text-slate-500">Mon humeur : <span className="text-lg">{myMood}</span></span>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {MOODS.map(m => (
                <button key={m.e} onClick={() => setMood(m.e)} data-testid={`mood-${m.label}`}
                        className={`px-2.5 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1 border transition ${
                          myMood === m.e ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-600 border-slate-200 hover:border-purple-300"
                        }`}>
                  <span className="text-base">{m.e}</span>{m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Composer */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <Avatar user={user} size={44}/>
              <div className="flex-1">
                <div className="flex items-center gap-1 mb-2">
                  <button onClick={() => setMode("post")} data-testid="journal-mode-post"
                          className={`text-xs px-3 py-1 rounded-full font-semibold ${mode === "post" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    📝 Publication
                  </button>
                  <button onClick={() => setMode("poll")} data-testid="journal-mode-poll"
                          className={`text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1 ${mode === "poll" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    <BarChart2 className="w-3 h-3"/> Sondage
                  </button>
                </div>
                <textarea data-testid="journal-content" ref={textareaRef} rows="2" value={content} onChange={onContentChange}
                          placeholder={mode === "poll" ? "Votre question…" : "Que se passe-t-il sur le terrain ? Tapez @ pour taguer un collègue"}
                          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm resize-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 outline-none"/>
                {mentionQuery && mentionResults.length > 0 && (
                  <div className="mt-1 rounded-xl border border-slate-200 bg-white shadow-lg divide-y divide-slate-100 max-h-64 overflow-y-auto" data-testid="mention-list">
                    {mentionResults.map(u => (
                      <button type="button" key={u.id} onClick={() => insertMention(u)} data-testid={`mention-${u.id}`}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left">
                        <Avatar user={u} size={26}/>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-800 truncate">{u.name}</div>
                          <div className="text-[10px] text-slate-500 truncate">{u.role?.toUpperCase()} · {u.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {mode === "poll" && (
                  <div className="mt-2 space-y-1.5">
                    {pollOpts.map((o, i) => (
                      <div key={i} className="flex gap-1">
                        <input value={o} onChange={e => setPollOpts(pollOpts.map((x, j) => j === i ? e.target.value : x))} placeholder={`Option ${i + 1}`}
                               className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm"/>
                        {pollOpts.length > 2 && (
                          <button type="button" onClick={() => setPollOpts(pollOpts.filter((_, j) => j !== i))} className="w-8 h-8 grid place-items-center rounded-lg text-rose-500 hover:bg-rose-50"><X className="w-3.5 h-3.5"/></button>
                        )}
                      </div>
                    ))}
                    {pollOpts.length < 6 && (
                      <button type="button" onClick={() => setPollOpts([...pollOpts, ""])} className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
                        <Plus className="w-3 h-3"/> Ajouter une option
                      </button>
                    )}
                  </div>
                )}
                <div className="mt-2"><PhotoUploader value={photos} onChange={setPhotos} testid="journal-photos"/></div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className="flex items-center gap-2 flex-1 min-w-[160px]">
                    <MapPin className="w-4 h-4 text-slate-400"/>
                    <input value={location} onChange={(e)=>setLocation(e.target.value)} placeholder="Lieu (ex : Intermarché Clamart)"
                           className="flex-1 px-3 py-1.5 rounded-full border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-100"/>
                  </div>
                  <div className="flex gap-1">
                    {Object.entries(tagMeta).map(([k, m]) => (
                      <button key={k} onClick={() => setTag(k)} className={`px-2 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${tag === k ? m.color : "bg-slate-50 text-slate-500 hover:bg-slate-100"}`}>
                        <m.Icon className="w-3 h-3"/> {m.label}
                      </button>
                    ))}
                  </div>
                  <button data-testid="journal-submit" onClick={submit} disabled={posting}
                          className="ml-auto flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60">
                    {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>} Publier
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">{posts.length} publication(s)</div>
            <button onClick={() => setSort(sort === "recent" ? "oldest" : "recent")} data-testid="journal-sort"
                    className="text-xs px-3 py-1.5 rounded-lg bg-white border border-slate-200 flex items-center gap-1 font-semibold text-slate-600">
              <ArrowUpDown className="w-3 h-3"/> {sort === "recent" ? "Plus récents" : "Plus anciens"}
            </button>
          </div>

          {grouped.map(([day, list]) => (
            <div key={day}>
              <div className="text-xs font-bold tracking-wider text-slate-400 uppercase mt-4 mb-2">{day}</div>
              <div className="space-y-3">
                {list.map(p => {
                  const m = tagMeta[p.tag] || tagMeta.info;
                  const author = usersById[p.user_id] || { name: p.user_name };
                  const isMine = p.user_id === user?.id;
                  const editing = editingId === p.id;
                  return (
                    <div key={p.id} data-testid={`journal-post-${p.id}`} className="bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-sm transition">
                      <div className="flex items-start gap-3">
                        <Avatar user={author} size={44}/>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-semibold text-slate-900 text-sm">{p.user_name}</div>
                            {author?.bio && <span className="text-[10px] text-slate-400 italic truncate max-w-[200px]">· {author.bio}</span>}
                            {p.kind === "poll" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1"><BarChart2 className="w-3 h-3"/> Sondage</span>}
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${m.color}`}>
                              <m.Icon className="w-3 h-3"/> {m.label}
                            </span>
                            {p.edited && <span className="text-[10px] text-slate-400 italic">(modifié)</span>}
                            {isMine && !editing && (
                              <div className="ml-auto flex items-center gap-1">
                                <button data-testid={`post-edit-${p.id}`} onClick={() => { setEditingId(p.id); setEditContent(p.content); }}
                                        className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50">
                                  <Pencil className="w-3.5 h-3.5"/>
                                </button>
                                <button data-testid={`post-delete-${p.id}`} onClick={() => deletePost(p)}
                                        className="w-7 h-7 grid place-items-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                                  <Trash2 className="w-3.5 h-3.5"/>
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                            <MapPin className="w-3 h-3"/>{p.location}
                            <span>·</span>
                            <span>{new Date(p.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          </div>
                          {editing ? (
                            <div className="mt-2 space-y-2">
                              <textarea data-testid={`post-edit-textarea-${p.id}`} value={editContent} onChange={e => setEditContent(e.target.value)}
                                        rows={3} className="w-full px-3 py-2 rounded-xl border border-blue-300 text-sm resize-none focus:ring-2 focus:ring-blue-100 outline-none"/>
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => { setEditingId(null); setEditContent(""); }} className="px-3 py-1 rounded-full text-xs text-slate-600 hover:bg-slate-100">Annuler</button>
                                <button data-testid={`post-save-${p.id}`} onClick={() => saveEdit(p)} className="px-3 py-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold flex items-center gap-1">
                                  <Check className="w-3 h-3"/> Enregistrer
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-slate-700 mt-2 leading-relaxed whitespace-pre-wrap">
                              {(p.content || "").split(/(@[A-Za-zÀ-ÿ0-9\-_.]+)/g).map((chunk, ci) =>
                                chunk.startsWith("@") ? <span key={ci} className="text-blue-600 font-semibold">{chunk}</span> : <span key={ci}>{chunk}</span>
                              )}
                            </div>
                          )}
                          <PhotoStrip paths={p.photos}/>
                          {p.kind === "poll" && p.poll && <Poll post={p} onVote={(i) => vote(p, i)} currentUserId={user?.id}/>}
                          <div className="mt-3 pt-2 border-t border-slate-100">
                            <ReactionBar post={p} onReact={(e) => react(p, e)} currentUserId={user?.id}/>
                            <CommentSection post={p} usersById={usersById}/>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {posts.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-400">Aucune publication.</div>}
          </>)}
        </div>

        {/* RIGHT SIDEBAR - Widgets (fixe pendant scroll, visible en desktop) */}
        <div className="space-y-4 lg:sticky lg:top-[76px] lg:self-start lg:max-h-[calc(100vh-92px)] lg:overflow-y-auto lg:pr-1 scrollbar-thin" data-testid="wasagram-sidebar">
          {/* ChatPanel removed from sidebar — moved to Messagerie tab */}
          {/* Weather */}
          {weather && (
            <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white p-4 shadow-md">
              <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Météo du jour · {weather.city || "France"}</div>
              <div className="flex items-center gap-3 mt-1">
                <div className="text-4xl">{weather.emoji || "☀️"}</div>
                <div>
                  <div className="font-display text-3xl font-bold">{weather.tmax != null ? Math.round(weather.tmax) : "—"}°</div>
                  <div className="text-xs opacity-90">{weather.label || "Ensoleillé"}</div>
                </div>
              </div>
            </div>
          )}

          {/* Top 10 corners par Wasa Score */}
          {top10.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="wasagram-top10">
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-amber-500"/>
                <div className="font-display text-sm font-semibold text-slate-900">Top 10 Wasa Score</div>
              </div>
              <div className="space-y-2">
                {top10.map((c, i) => {
                  const s = c.score || 0;
                  const clr = s >= 80 ? "text-emerald-600" : s >= 60 ? "text-blue-600" : s >= 40 ? "text-amber-600" : "text-rose-600";
                  return (
                    <div key={c.corner_key || c.corner} className="flex items-center gap-2 text-xs">
                      <div className={`w-6 h-6 rounded-full grid place-items-center font-bold text-[10px] ${i < 3 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0 truncate font-semibold text-slate-700">{c.corner}</div>
                      <div className={`font-bold ${clr}`}>{s}/100</div>
                      {c.badge === "star" && <span className="text-[10px]">⭐</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top posts */}
          {topPosts.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-4 h-4 text-rose-500"/>
                <div className="font-display text-sm font-semibold text-slate-900">Publications qui buzzent</div>
              </div>
              <div className="space-y-3">
                {topPosts.slice(0, 4).map(p => {
                  const rx = Object.values(p.reactions || {}).reduce((s, a) => s + a.length, 0);
                  const author = usersById[p.user_id] || { name: p.user_name };
                  return (
                    <div key={p.id} className="flex gap-2 items-start">
                      <Avatar user={author} size={30}/>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-800 truncate">{p.user_name}</div>
                        <div className="text-xs text-slate-500 line-clamp-2 leading-snug">{p.content}</div>
                        <div className="mt-1 flex items-center gap-1 text-[10px] font-bold text-rose-600">
                          <Flame className="w-2.5 h-2.5"/>{rx} réactions
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Last activity per user */}
          {lastActivity.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4 text-blue-500"/>
                <div className="font-display text-sm font-semibold text-slate-900">Qui fait quoi ?</div>
              </div>
              <div className="space-y-2.5">
                {lastActivity.slice(0, 8).map(la => (
                  <div key={la.user_id} className="flex gap-2 items-start">
                    <div className="relative">
                      <Avatar user={{ name: la.user_name, avatar_url: la.avatar_url }} size={30}/>
                      {la.mood?.emoji && (
                        <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-white grid place-items-center text-[10px] border border-slate-200">
                          {la.mood.emoji}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{la.user_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{la.visit_type}{la.description ? ` — ${la.description}` : ""}</div>
                      <div className="text-[10px] text-slate-400">{la.date ? new Date(la.date).toLocaleDateString("fr-FR") : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky bottom tab bar (mobile-friendly + always visible during scroll) */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)] px-3 py-2 lg:hidden" data-testid="wasagram-tabs-bottom">
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          <TabButton active={tab === "fil"} onClick={() => setTab("fil")} testid="tab-fil-bottom"
                     icon={Newspaper} label="Fil d'actualité" badge={tab === "fil" ? 0 : unreadFil}/>
          <TabButton active={tab === "messagerie"} onClick={() => setTab("messagerie")} testid="tab-messagerie-bottom"
                     icon={MessageCircle} label="Messagerie" badge={tab === "messagerie" ? 0 : unreadChats}/>
        </div>
      </div>
    </div>
  );
}

// ═════ Sticky tab button with badge ═════
function TabButton({ active, onClick, testid, icon: Icon, label, badge = 0 }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`relative flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>
      <div className="relative">
        <Icon className="w-4 h-4"/>
        {badge > 0 && (
          <span data-testid={`${testid}-badge`}
                className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center shadow-sm border border-white">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
